import React, { useState, useEffect } from 'react';
import { Facebook, Linkedin, Instagram, Phone, Mail, Send, MapPin, X, CheckCircle, AlertCircle, Music, Youtube, MessageCircle } from 'lucide-react';
import { submitLead, ROLES, getContactRole } from '../../lib/leads';

// TikTok icon component (since it's not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Twitter/X icon component
const TwitterXIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Spotify icon component
const SpotifyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0m3.669 11.538a.5.5 0 0 1-.686.165c-1.879-1.147-4.243-1.407-7.028-.77a.499.499 0 0 1-.222-.973c3.048-.696 5.662-.397 7.77.892a.5.5 0 0 1 .166.686m.979-2.178a.624.624 0 0 1-.858.205c-2.15-1.321-5.428-1.704-7.972-.932a.625.625 0 0 1-.362-1.194c2.905-.881 6.517-.454 8.986 1.063a.624.624 0 0 1 .206.858m.084-2.268C10.154 5.56 5.9 5.419 3.438 6.166a.748.748 0 1 1-.434-1.432c2.825-.857 7.523-.692 10.492 1.07a.747.747 0 1 1-.764 1.288"/>
  </svg>
);

const NEXT_STEP = {
  'Farmer': 'We will call or WhatsApp you to talk about training, insurance and loans.',
  'Farmer group or chama': 'We will call you to talk about training and onboarding your group.',
  'Cooperative': 'Our partnerships team will contact you to arrange a meeting.',
  'Insurance company': 'Our partnerships team will contact you to arrange an introductory meeting.',
  'Lender': 'Our partnerships team will contact you to arrange an introductory meeting.',
  'Veterinary office': 'We will call you to talk about joining and serving farmers.',
  'Buyer or processor': 'We will contact you and introduce you to farmer groups.',
  'Government or university': 'Our partnerships team will contact you to arrange an introductory meeting.',
};

const EMPTY = { name: '', role: '', phone: '', email: '', message: '', consent: false };

const SOCIAL_LINKS = [
  { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/ImfuyoKenya' },
  { icon: TwitterXIcon, label: 'X (Twitter)', href: 'https://x.com/Imfuyousajili' },
  { icon: TikTokIcon, label: 'TikTok', href: 'https://www.tiktok.com/@imfuyo.kenya' },
  { icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/imfuyo-usajili' },
  { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/imfuyo.farm/' },
  { icon: SpotifyIcon, label: 'Spotify', href: 'https://open.spotify.com/show/0tBRxfkKYvil2tWiiEtq2i?si=ad9a82213a864f8e' },
  { icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/@Imfuyo' },
  { icon: MessageCircle, label: 'WhatsApp', href: 'https://wa.me/254110563185' },
  { icon: Linkedin, label: 'Imfuyo Foundation on LinkedIn', href: 'https://www.linkedin.com/showcase/imfuyo-foundation/?viewAsMember=true' },
];

const CONTACT_INFO = [
  { icon: Phone, label: 'Call us', value: '+254 110 563 185', href: 'tel:+254110563185' },
  { icon: MessageCircle, label: 'WhatsApp', value: '+254 110 563 185', href: 'https://wa.me/254110563185' },
  { icon: Mail, label: 'Email', value: 'hello@imfuyo.farm', href: 'mailto:hello@imfuyo.farm' },
  { icon: Mail, label: 'Imfuyo Foundation', value: 'foundation@imfuyo.farm', href: 'mailto:foundation@imfuyo.farm' },
  { icon: MapPin, label: 'Nairobi office', value: 'Nairobi, Kenya' },
  { icon: MapPin, label: 'US office', value: 'Chatham, New Jersey' },
];

function ContactList() {
  return (
    <ul className="cf-list">
      {CONTACT_INFO.map((c, i) => {
        const Icon = c.icon;
        return (
          <li key={i}>
            <span className="cf-ic"><Icon size={20} /></span>
            <span>
              <small>{c.label}</small>
              {c.href ? <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{c.value}</a> : <b>{c.value}</b>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Social() {
  return (
    <>
      <p className="cf-conn">Follow us</p>
      <div className="cf-social">
        {SOCIAL_LINKS.map((l, i) => {
          const Icon = l.icon;
          return <a key={i} href={l.href} target="_blank" rel="noopener noreferrer" aria-label={l.label} title={l.label}><Icon className="cf-sico" size={20} /></a>;
        })}
      </div>
    </>
  );
}

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState(EMPTY);
  const [sentTo, setSentTo] = useState({ name: '', role: '' });

  useEffect(() => {
    const r = getContactRole();
    if (r) setFormData(prev => ({ ...prev, role: r }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    if (!formData.phone.trim() && !formData.email.trim()) {
      setFormError('Please give us a phone number or an email address so we can reach you.');
      return;
    }
    setFormError('');
    setIsSubmitting(true);
    try {
      await submitLead(formData);
      setSentTo({ name: formData.name.trim().split(' ')[0], role: formData.role });
      setSubmitStatus('success');
      setFormData(EMPTY);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (err) { /* ignore */ }
    } catch (error) {
      console.error('Error submitting message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="nx">
        <section id="contact" style={{ background: '#F4F0E6' }}>
          <div className="wrap sec" style={{ maxWidth: 860 }}>
            <p className="eb">Message received</p>
            <h2 className="h2">Thank you{sentTo.name ? `, ${sentTo.name}` : ''}. We have your message.</h2>
            <p className="lead" style={{ marginBottom: 28 }}>{NEXT_STEP[sentTo.role] || 'We will contact you using the details you gave us.'} If it is urgent, reach us now:</p>
            <div className="cf-actions">
              <a className="btn btn-o" href="tel:+254110563185"><Phone size={18} aria-hidden="true" /> Call +254 110 563 185</a>
              <a className="btn btn-g" href="https://wa.me/254110563185" target="_blank" rel="noopener noreferrer"><MessageCircle size={18} aria-hidden="true" /> WhatsApp us</a>
              <a className="btn btn-d" href="mailto:hello@imfuyo.farm"><Mail size={18} aria-hidden="true" /> hello@imfuyo.farm</a>
            </div>
            <div className="cf-card" style={{ marginTop: 32 }}>
              <ContactList />
              <Social />
            </div>
            <p style={{ marginTop: 24 }}><button type="button" className="cf-linkbtn" onClick={() => setSubmitStatus(null)}>Send another message</button></p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="nx">
      <section id="contact" style={{ background: '#F4F0E6' }}>
        <div className="wrap sec">
          <p className="eb">Contact</p>
          <h2 className="h2">Let&rsquo;s talk. Tell us who you are and how to reach you.</h2>
          <p className="lead" style={{ marginBottom: 40 }}>Farmers, groups, cooperatives, insurers, lenders, veterinary offices, buyers and partners are all welcome. It takes a minute, and we will come back to you.</p>
          <div className="cf-grid">
            <form className="cf-card cf-form" onSubmit={handleSubmit}>
              <h3>Send us a message</h3>
              <label>Your name<input name="name" value={formData.name} onChange={handleInputChange} required autoComplete="name" /></label>
              <label>I am a<select name="role" value={formData.role} onChange={handleInputChange} required>
                <option value="" disabled>Choose one</option>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select></label>
              <div className="cf-row">
                <label>Phone number<input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} autoComplete="tel" placeholder="+254..." /></label>
                <label>Email address<input type="email" name="email" value={formData.email} onChange={handleInputChange} autoComplete="email" /></label>
              </div>
              <p className="cf-hint">Phone or email, whichever you prefer. One is enough.</p>
              <label>How can we help?<textarea name="message" rows={4} value={formData.message} onChange={handleInputChange} required /></label>
              <label className="cf-check cf-consent"><input type="checkbox" name="consent" checked={formData.consent} onChange={handleInputChange} required />
                <span>I agree that Imfuyo may contact me about this enquiry and keep these details. Imfuyo is registered with Kenya&rsquo;s Office of the Data Protection Commissioner.</span></label>
              {formError && <p className="cf-msg bad" role="alert"><AlertCircle size={18} /> {formError}</p>}
              {submitStatus === 'error' && <p className="cf-msg bad" role="alert"><AlertCircle size={18} /> The message could not be sent. Please try again, or call or WhatsApp us on +254 110 563 185.</p>}
              <button type="submit" className="btn btn-o" disabled={isSubmitting} style={{ border: 0, alignSelf: 'flex-start' }}>
                {isSubmitting ? 'Sending...' : 'Send message'} <Send size={18} aria-hidden="true" />
              </button>
            </form>
            <div className="cf-card">
              <h3 className="cf-side">Or reach us directly</h3>
              <ContactList />
              <Social />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
